/**
 * Modal pour sélectionner les sources ACHAT à ignorer lors de l'attribution
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Search, Zap, Filter, AlertTriangle } from 'lucide-react';

export function MatcherFilterModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  tickets = [],
  isLoading = false 
}) {
  const [selectedSources, setSelectedSources] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Calculer les sources uniques avec leur nombre de tickets non attribués
  const sourcesWithCounts = useMemo(() => {
    const counts = {};
    tickets.forEach(t => {
      if (t.type === 'ACHAT' && !t.attribue && t.statut !== 'VALIDE') {
        const source = t.source || 'N/A';
        counts[source] = (counts[source] || 0) + 1;
      }
    });
    
    return Object.entries(counts)
      .map(([source, count]) => ({ source, count }))
      .sort((a, b) => b.count - a.count);
  }, [tickets]);

  // Filtrer par recherche
  const filteredSources = useMemo(() => {
    if (!searchTerm) return sourcesWithCounts;
    const term = searchTerm.toLowerCase();
    return sourcesWithCounts.filter(s => 
      s.source.toLowerCase().includes(term)
    );
  }, [sourcesWithCounts, searchTerm]);

  // Toggle source selection
  const toggleSource = (source) => {
    setSelectedSources(prev => 
      prev.includes(source) 
        ? prev.filter(s => s !== source)
        : [...prev, source]
    );
  };

  // Sélectionner/Désélectionner tout
  const toggleAll = () => {
    if (selectedSources.length === filteredSources.length) {
      setSelectedSources([]);
    } else {
      setSelectedSources(filteredSources.map(s => s.source));
    }
  };

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setSelectedSources([]);
      setSearchTerm('');
    }
  }, [isOpen]);

  // Calculer les stats
  const totalAchatDispo = sourcesWithCounts.reduce((sum, s) => sum + s.count, 0);
  const excludedCount = selectedSources.reduce((sum, source) => {
    const found = sourcesWithCounts.find(s => s.source === source);
    return sum + (found?.count || 0);
  }, 0);
  const remainingCount = totalAchatDispo - excludedCount;

  const handleConfirm = () => {
    onConfirm(selectedSources);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-zinc-900 border-zinc-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Filter className="h-5 w-5 text-amber-400" />
            Attribution Filtrée
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Info */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <p className="text-amber-300 text-sm">
              Sélectionnez les sources ACHAT à <strong>ignorer</strong> lors de l'attribution.
              Les billets de ces sources ne seront pas utilisés.
            </p>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              placeholder="Rechercher une source..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>

          {/* Select All */}
          <div className="flex items-center justify-between px-2">
            <button
              onClick={toggleAll}
              className="text-xs text-zinc-400 hover:text-white transition-colors"
            >
              {selectedSources.length === filteredSources.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
            <span className="text-xs text-zinc-500">
              {selectedSources.length} source(s) ignorée(s)
            </span>
          </div>

          {/* Sources List */}
          <div className="max-h-64 overflow-y-auto space-y-1 border border-zinc-700 rounded-lg p-2">
            {filteredSources.map(({ source, count }) => {
              const isSelected = selectedSources.includes(source);
              return (
                <label
                  key={source}
                  className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                    isSelected 
                      ? 'bg-red-500/20 border border-red-500/40' 
                      : 'hover:bg-zinc-800 border border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleSource(source)}
                      className="border-zinc-500 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                    />
                    <span className={`text-sm ${isSelected ? 'text-red-300 line-through' : 'text-white'}`}>
                      {source}
                    </span>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    isSelected ? 'bg-red-500/30 text-red-300' : 'bg-zinc-700 text-zinc-400'
                  }`}>
                    {count} billets
                  </span>
                </label>
              );
            })}
            {filteredSources.length === 0 && (
              <p className="text-center text-zinc-500 py-4">Aucune source trouvée</p>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-zinc-800 rounded-lg p-2">
              <p className="text-lg font-bold text-white">{totalAchatDispo}</p>
              <p className="text-xs text-zinc-400">ACHAT dispo</p>
            </div>
            <div className="bg-red-500/20 rounded-lg p-2">
              <p className="text-lg font-bold text-red-400">-{excludedCount}</p>
              <p className="text-xs text-red-300">Ignorés</p>
            </div>
            <div className="bg-emerald-500/20 rounded-lg p-2">
              <p className="text-lg font-bold text-emerald-400">{remainingCount}</p>
              <p className="text-xs text-emerald-300">Utilisés</p>
            </div>
          </div>

          {/* Warning if all excluded */}
          {remainingCount === 0 && selectedSources.length > 0 && (
            <div className="flex items-center gap-2 bg-red-500/20 border border-red-500/40 rounded-lg p-3">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <p className="text-red-300 text-sm">
                Attention: Tous les ACHAT seront ignorés !
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2 mt-4">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
          >
            Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || remainingCount === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {isLoading ? (
              <>
                <Zap className="h-4 w-4 mr-2 animate-spin" />
                Attribution...
              </>
            ) : (
              <>
                <Zap className="h-4 w-4 mr-2" />
                {selectedSources.length > 0 
                  ? `Attribuer (sans ${selectedSources.length} source${selectedSources.length > 1 ? 's' : ''})` 
                  : 'Attribuer tous'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MatcherFilterModal;
